import classnames from 'classnames'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { RegisterButton } from '../ProgramPresentationPage'
import { displayWithCurrency } from '../../../../../utility/currency'

import styles from './ProgramMembership.module.scss'
import { ReactComponent as CheckmarkIcon } from '../../../../../assets/images/common/icons/checkmark-icon.svg'
import curriculumImage from '../../../../../assets/images/membership/curriculum.png'
import gpsImage from '../../../../../assets/images/membership/gps.png'
import practiceImage from '../../../../../assets/images/membership/practice.png'
import journalImage from '../../../../../assets/images/membership/journal.png'
import dashboardImage from '../../../../../assets/images/membership/dashboard.png'
import programsImage from '../../../../../assets/images/membership/programs.png'
import communityImage from '../../../../../assets/images/membership/community.png'

const FEATURE_GROUPS = [
  {
    key: 'tools',
    sections: [
      { key: 'curriculum', image: curriculumImage, checks: 4 },
      { key: 'gps', image: gpsImage, checks: 3, reversed: true },
      { key: 'practice', image: practiceImage, checks: 4 },
      { key: 'journal', image: journalImage, checks: 4, reversed: true },
    ],
  },
  {
    key: 'moreThanTools',
    sections: [
      { key: 'dashboard', image: dashboardImage, checks: 4 },
      { key: 'programs', image: programsImage, checks: 4, reversed: true },
      { key: 'community', image: communityImage, checks: 3 },
    ],
  },
]

const ProgramMembership = () => {
  const { t } = useTranslation()
  const membership = useSelector(state => state.programPresentation.course.membership)
  const plans = membership?.plans || []
  const cheapest = plans[0]
  const individual = plans.find(plan => plan.tier === 'individual')
  const family = plans.find(plan => plan.tier === 'family')

  return (
    <div className={classnames('p-3', styles['program-membership'])}>
      <div className={styles['header-row']}>
        <p className={classnames('mb-0', styles.eyebrow)}>{t('programs.landing.membership.eyebrow')}</p>
        {!!cheapest && (
          <span className={styles['price-pill']}>
            {t('programs.landing.membership.startingAtPill', { price: displayWithCurrency(cheapest.amount, cheapest.currency) })}
          </span>
        )}
      </div>
      <h2 className={styles.title}>{t('programs.landing.membership.title')}</h2>
      <p className={styles.intro}>{t('programs.landing.membership.intro')}</p>

      {FEATURE_GROUPS.map(group => (
        <div key={group.key} className={styles['feature-group']}>
          <p className={classnames('mb-1', styles.eyebrow)}>{t(`programs.landing.membership.${group.key}.eyebrow`)}</p>
          <p className={styles['group-title']}>{t(`programs.landing.membership.${group.key}.title`)}</p>
          <p className={styles['group-subtitle']}>{t(`programs.landing.membership.${group.key}.subtitle`)}</p>

          {group.sections.map(section => (
            <div key={section.key} className={classnames(styles['feature-row'], { [styles.reversed]: section.reversed })}>
              <div className={styles['feature-image']}>
                <img src={section.image} alt={t(`programs.landing.membership.sections.${section.key}.title`)} />
              </div>
              <div>
                <p className={classnames('mb-1', styles.eyebrow)}>{t(`programs.landing.membership.sections.${section.key}.eyebrow`)}</p>
                <p className={styles['feature-title']}>{t(`programs.landing.membership.sections.${section.key}.title`)}</p>
                <p className={styles['feature-subtitle']}>{t(`programs.landing.membership.sections.${section.key}.subtitle`)}</p>
                <ul className={styles.checklist}>
                  {Array.from({ length: section.checks }, (_, index) => (
                    <li key={index}>
                      <CheckmarkIcon className={styles['check-icon']} aria-hidden='true' />
                      <span>{t(`programs.landing.membership.sections.${section.key}.checks.${index + 1}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      ))}

      <div className={styles['cta-banner']}>
        <div>
          {!!individual && !!family && (
            <p className={styles['cta-title']}>
              {t('programs.landing.membership.cta.title', {
                individual: displayWithCurrency(individual.amount, individual.currency),
                family: displayWithCurrency(family.amount, family.currency),
              })}
            </p>
          )}
          <p className={styles['cta-subtitle']}>{t('programs.landing.membership.cta.subtitle')}</p>
        </div>
        <div className={styles['cta-button']}>
          <RegisterButton />
        </div>
      </div>
    </div>
  )
}

export default ProgramMembership
